/**
 * AccountDetailsTab Component
 *
 * Edit user's own account details (AspNetUserDetail).
 * Features: edit firstName, surname, cellphone, avatar, thumbnail.
 * Email is read-only (cannot be changed by user).
 * Note: Gender is managed in MemberInfoTab as it's part of the Member entity.
 */

import { useState, useEffect } from 'react';
import { useForm } from '@mantine/form';
import { IconDeviceFloppy, IconX } from '@tabler/icons-react';
import {
  Stack,
  Card,
  Group,
  Button,
  TextInput,
  Select,
  Grid,
  Avatar,
  Text,
} from '../../../components/ui';
import { toast } from '../../../components/ui';
import { useUserService } from '@actschurch/ccms-sdk';
import { FileUploader } from '../../../components/ccms-components/file-management/components/FileUploader';
import type { FileInfo } from '../../../components/ccms-components/file-management/types';
import type { ProfileTabProps } from '../types';

export function AccountDetailsTab({ userId, user, onUpdate }: ProfileTabProps) {
  const userService = useUserService();
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [uploading, setUploading] = useState(false);

  const form = useForm({
    initialValues: {
      firstName: user?.firstName || '',
      surname: user?.surname || '',
      cellphone: user?.cellphone || '',
      avatar: user?.avatar || '',
      thumbnail: user?.thumbnail || '',
    },
    validate: {
      firstName: (value) => {
        if (!value || value.trim() === '') return 'First name is required';
        if (value.trim().length < 2) return 'First name must be at least 2 characters';
        return null;
      },
      surname: (value) => {
        if (!value || value.trim() === '') return 'Surname is required';
        if (value.trim().length < 2) return 'Surname must be at least 2 characters';
        return null;
      },
      cellphone: (value) => {
        if (value && value.trim() !== '') {
          if (!/^[\d\s\-\+\(\)]+$/.test(value)) {
            return 'Invalid phone number format';
          }
        }
        return null;
      },
    },
  });

  // Update form when user data changes
  useEffect(() => {
    if (user) {
      form.setValues({
        firstName: user.firstName || '',
        surname: user.surname || '',
        cellphone: user.cellphone || '',
        avatar: user.avatar || '',
        thumbnail: user.thumbnail || '',
      });
    }
  }, [user]);

  // Track changes
  useEffect(() => {
    const hasChanged =
      form.values.firstName !== (user?.firstName || '') ||
      form.values.surname !== (user?.surname || '') ||
      form.values.cellphone !== (user?.cellphone || '') ||
      form.values.avatar !== (user?.avatar || '') ||
      form.values.thumbnail !== (user?.thumbnail || '');

    setHasChanges(hasChanged);
  }, [form.values, user]);

  /**
   * Handle save changes
   */
  const handleSave = async (values: typeof form.values) => {
    setSaving(true);
    try {
      await toast.promise(
        userService.updateCurrentUser({
          firstName: values.firstName.trim(),
          surname: values.surname.trim(),
          cellphone: values.cellphone.trim() || undefined,
          avatar: values.avatar.trim() || undefined,
          thumbnail: values.thumbnail.trim() || undefined,
        }),
        {
          loading: 'Saving account details...',
          success: 'Account details updated successfully',
          error: (err: any) => err.message || 'Failed to update account details',
        }
      );

      setHasChanges(false);
      onUpdate?.();
    } finally {
      setSaving(false);
    }
  };

  /**
   * Handle reset form
   */
  const handleReset = () => {
    form.setValues({
      firstName: user?.firstName || '',
      surname: user?.surname || '',
      cellphone: user?.cellphone || '',
      avatar: user?.avatar || '',
      thumbnail: user?.thumbnail || '',
    });
    setHasChanges(false);
  };

  /**
   * Handle avatar upload completion
   */
  const handleAvatarUpload = (files: FileInfo[]) => {
    if (files.length > 0 && files[0].url) {
      form.setFieldValue('avatar', files[0].url);
      toast.success('Avatar uploaded successfully');
    }
  };

  /**
   * Handle avatar upload error
   */
  const handleAvatarUploadError = (error: Error) => {
    toast.error(`Failed to upload avatar: ${error.message}`);
  };

  /**
   * Handle thumbnail upload completion
   */
  const handleThumbnailUpload = (files: FileInfo[]) => {
    if (files.length > 0 && files[0].url) {
      form.setFieldValue('thumbnail', files[0].url);
      toast.success('Thumbnail uploaded successfully');
    }
  };

  /**
   * Handle thumbnail upload error
   */
  const handleThumbnailUploadError = (error: Error) => {
    toast.error(`Failed to upload thumbnail: ${error.message}`);
  };

  return (
    <form onSubmit={form.onSubmit(handleSave)}>
      <Stack gap="lg">
        <Card withBorder>
          <Stack gap="md">
            <Text size="lg" fw={600}>
              Account Information
            </Text>

            {/* Avatar */}
            <div>
              <Text size="sm" fw={500} mb="xs">
                Profile Avatar
              </Text>
              <Group align="flex-start">
                <Avatar src={form.values.avatar} size={120} radius="md">
                  {form.values.firstName?.charAt(0)}{form.values.surname?.charAt(0)}
                </Avatar>
                <Stack gap="xs" style={{ flex: 1 }}>
                  <FileUploader
                    domain="User"
                    recordId={userId}
                    variant="button"
                    accept={['image/*']}
                    maxSize={5 * 1024 * 1024} // 5MB
                    onUploadComplete={handleAvatarUpload}
                    onUploadError={handleAvatarUploadError}
                    gradientVariant="primary"
                  />
                  <Text size="xs" c="dimmed">
                    Upload an image (max 5MB). Accepted formats: JPG, PNG, GIF
                  </Text>
                  {form.values.avatar && (
                    <Group gap="xs">
                      <Text size="xs" c="dimmed">
                        Current URL:
                      </Text>
                      <Text size="xs" c="blue" style={{ wordBreak: 'break-all' }}>
                        {form.values.avatar}
                      </Text>
                    </Group>
                  )}
                </Stack>
              </Group>
            </div>

            {/* Name Fields */}
            <Grid>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  label="First Name"
                  placeholder="John"
                  required
                  {...form.getInputProps('firstName')}
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  label="Surname"
                  placeholder="Doe"
                  required
                  {...form.getInputProps('surname')}
                />
              </Grid.Col>
            </Grid>

            {/* Thumbnail Upload */}
            <div>
              <Text size="sm" fw={500} mb="xs">
                Profile Thumbnail (Optional)
              </Text>
              <Group align="flex-start">
                <Avatar src={form.values.thumbnail} size={80} radius="md">
                  {form.values.firstName?.charAt(0)}{form.values.surname?.charAt(0)}
                </Avatar>
                <Stack gap="xs" style={{ flex: 1 }}>
                  <FileUploader
                    domain="User"
                    recordId={userId}
                    variant="button"
                    accept={['image/*']}
                    maxSize={2 * 1024 * 1024} // 2MB for thumbnail
                    onUploadComplete={handleThumbnailUpload}
                    onUploadError={handleThumbnailUploadError}
                    gradientVariant="secondary"
                  />
                  <Text size="xs" c="dimmed">
                    Upload a smaller thumbnail image (max 2MB). Used in lists and small previews.
                  </Text>
                  {form.values.thumbnail && (
                    <Group gap="xs">
                      <Text size="xs" c="dimmed">
                        Current URL:
                      </Text>
                      <Text size="xs" c="blue" style={{ wordBreak: 'break-all' }}>
                        {form.values.thumbnail}
                      </Text>
                    </Group>
                  )}
                </Stack>
              </Group>
            </div>

            {/* Contact Fields */}
            <Grid>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  label="Email"
                  value={user?.email || ''}
                  disabled
                  readOnly
                  description="Email cannot be changed. Contact an administrator if you need to update it."
                />
              </Grid.Col>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  label="Cellphone"
                  placeholder="+1 234 567 8900"
                  {...form.getInputProps('cellphone')}
                />
              </Grid.Col>
            </Grid>

            {/* Account Meta Info */}
            <Grid>
              <Grid.Col span={{ base: 12, sm: 6 }}>
                <TextInput
                  label="User ID"
                  value={user?.id || userId}
                  disabled
                  readOnly
                />
              </Grid.Col>
              {user?.createdDate && (
                <Grid.Col span={{ base: 12, sm: 6 }}>
                  <TextInput
                    label="Account Created"
                    value={
                      new Date(user.createdDate).toLocaleDateString('en-ZA', {
                        year: 'numeric',
                        month: 'long',
                        day: 'numeric',
                      })
                    }
                    disabled
                    readOnly
                  />
                </Grid.Col>
              )}
            </Grid>

          </Stack>
        </Card>

        {/* Action Buttons */}
        {hasChanges && (
          <Group justify="flex-end">
            <Button variant="light" leftSection={<IconX size={16} />} onClick={handleReset}>
              Cancel
            </Button>
            <Button
              type="submit"
              leftSection={<IconDeviceFloppy size={16} />}
              loading={saving}
              gradientVariant="primary"
            >
              Save Changes
            </Button>
          </Group>
        )}
      </Stack>
    </form>
  );
}
